import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Layout from '@/components/Layout';
import { PageContainer } from '@/components/ui/PageContainer';
import { PageHeader } from '@/components/ui/PageHeader';
import { MapPin, Users, Briefcase, Loader2, Save, Upload, Image, Trash2, Settings } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegionalAdminSettings() {
  const [user, setUser] = useState(null);
  const [region, setRegion] = useState(null);
  const [employers, setEmployers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [userRes, settingsRes, analyticsRes, employersRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/regional/settings`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${BACKEND_URL}/api/regional/analytics`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${BACKEND_URL}/api/regional/employers`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setUser(userRes.data);
        setAnalytics(analyticsRes.data);
        setEmployers(employersRes.data || []);
        if (settingsRes.data) {
          setRegion(settingsRes.data);
          setForm({
            name: settingsRes.data.name || '',
            description: settingsRes.data.description || ''
          });
        }
      } catch { /* auth redirect */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Region name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/regional/settings`, {
        name: form.name.trim(),
        description: form.description.trim()
      }, { withCredentials: true });
      setRegion(res.data);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/regional/settings/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRegion(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const approvedCount = employers.filter(e => e.status === 'approved').length;
  const pendingCount = employers.filter(e => e.status === 'pending').length;

  if (loading) {
    return (
      <Layout user={user}>
        <PageContainer>
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-brand" />
          </div>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <PageContainer>
        <PageHeader
          icon={Settings}
          title="Regional Settings"
          subtitle={`Settings and branding for ${region?.name || 'your region'}`}
        />

        {/* Account */}
        <Card className="p-6 border border-slate-200 mb-6" data-testid="admin-account-card">
          <h2 className="text-base font-semibold text-slate-900 mb-4">Account</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Name</p>
              <p className="text-sm text-slate-900 font-medium">{user?.name || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Email</p>
              <p className="text-sm text-slate-900 font-medium">{user?.email || 'Not set'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Role</p>
              <p className="text-sm text-slate-900 font-medium">Regional Administrator</p>
            </div>
          </div>
        </Card>

        {/* Region Branding — Logo Upload */}
        <Card className="p-6 border border-slate-200 mb-6" data-testid="region-logo-card">
          <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Image className="w-4 h-4 text-brand" /> Region Logo
          </h2>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {region?.logo_url ? (
                <div className="relative group">
                  <img
                    data-testid="region-logo-preview"
                    src={region.logo_url.startsWith('data:') ? region.logo_url : `${BACKEND_URL}${region.logo_url}`}
                    alt={`${region.name || 'Region'} logo`}
                    className="w-28 h-28 object-contain rounded-lg border border-slate-200 bg-white p-2"
                  />
                </div>
              ) : (
                <div className="w-28 h-28 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400">
                  <Image className="w-8 h-8 mb-1" />
                  <span className="text-xs">No logo</span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-600 mb-3">
                Upload your region's logo. It will appear on employer-facing pages and communications.
                Supported formats: JPG, PNG, GIF, WebP. Max 5MB.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button
                data-testid="upload-logo-btn"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-brand text-brand hover:bg-brand-light"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> {region?.logo_url ? 'Replace Logo' : 'Upload Logo'}</>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Region Details — Editable */}
        {region && (
          <Card className="p-6 border border-slate-200 mb-6" data-testid="region-details-card">
            <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-brand" /> Region Details
            </h2>
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="region_name" className="text-slate-700 text-sm font-medium">Region Name</Label>
                  <Input
                    id="region_name"
                    data-testid="region-name-input"
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1.5"
                    placeholder="Region name"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm font-medium">State</Label>
                  <p className="mt-1.5 text-sm text-slate-900 h-10 flex items-center">{region.state || 'Not set'}</p>
                </div>
              </div>
              <div>
                <Label htmlFor="region_desc" className="text-slate-700 text-sm font-medium">Description</Label>
                <Textarea
                  id="region_desc"
                  data-testid="region-desc-input"
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1.5 min-h-[100px]"
                  placeholder="Describe your region..."
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  data-testid="save-region-settings-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-brand hover:bg-brand-hover text-white"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Region Overview Stats */}
        <Card className="p-6 border border-slate-200" data-testid="region-stats-card">
          <h2 className="text-base font-semibold text-slate-900 mb-4">Region Overview</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <Users className="w-5 h-5 text-brand mx-auto mb-1" />
              <p className="text-2xl font-bold text-slate-900">{approvedCount}</p>
              <p className="text-xs text-slate-500">Approved Employers</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <Users className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-slate-900">{pendingCount}</p>
              <p className="text-xs text-slate-500">Pending Review</p>
            </div>
            {analytics?.jobs && (
              <>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Briefcase className="w-5 h-5 text-brand mx-auto mb-1" />
                  <p className="text-2xl font-bold text-slate-900">{analytics.jobs.active || 0}</p>
                  <p className="text-xs text-slate-500">Active Jobs</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Briefcase className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-slate-900">{analytics.jobs.total || 0}</p>
                  <p className="text-xs text-slate-500">Total Jobs</p>
                </div>
              </>
            )}
          </div>
        </Card>
      </PageContainer>
    </Layout>
  );
}
